<template>
  <v-container class="main-container d-flex flex-column justify-center">
    <!-- Checkout texts -->
    <RegInfoSummary v-if="!paymentSuccessful" />

    <!-- PAYMENT SUCCESS -->
    <div v-if="paymentSuccessful && !saveFailed" class="text-center">
      <v-icon color="#c8e6c9" size="100"> $vuetify.icons.checkboxMarkedCircle </v-icon>
      <div class="text-checkout-sum mt-3">{{ $t("payment_successful_1") }}</div>
      <div class="text-checkout-details mt-2">
        {{ $t("payment_successful_2") }}
      </div>
      <div class="text-checkout-details mt-2" v-if="documentUploadSuccess">
        {{ $t("upload_success") }}
      </div>
    </div>

    <!-- DOCUMENT UPLOAD FAILED -->
    <v-row v-if="documentUploadError && !documentUploadSuccess">
      <v-col cols="12" md="10" offset="0" offset-md="1" class="pt-4 pb-5">
        <v-alert dense color="#f0404f" outlined>
          <v-icon left color="#f0404f"> $vuetify.icons.closeCircle </v-icon
          ><span>{{ $t("upload_error") }} ({{ documentUploadError }})</span></v-alert
        >
      </v-col>
    </v-row>

    <div v-if="!paymentSuccessful">
      <!-- EXPRESS CHECKOUT ELEMENT -->
      <v-row>
        <v-col cols="12" md="10" offset="0" offset-md="1">
          <img src="@/assets/stripe-color.svg" class="stripe-logo mb-2" alt="Stripe Logo" />
          <div id="express-checkout-element">
            <!-- Mount the Express Checkout Element here -->
          </div>
        </v-col>
      </v-row>

      <!-- PAYMENT FAILED -->
      <v-row v-if="paymentFailed" class="mb-2">
        <v-col cols="12" md="10" offset="0" offset-md="1" class="pt-4 pb-5">
          <v-alert dense color="#f0404f" outlined>
            <v-icon left color="#f0404f"> $vuetify.icons.closeCircle</v-icon
            ><span>{{ $t("payment_failed") }}</span></v-alert
          >
        </v-col>
      </v-row>

      <!-- SAVE FAILED -->
      <v-row v-if="saveFailed">
        <v-col cols="12" md="10" offset="0" offset-md="1" class="pt-4 pb-5">
          <v-alert dense color="#f0404f" outlined>
            <v-icon left color="#f0404f"> $vuetify.icons.closeCircle </v-icon
            ><span
              >{{ $t("save_failed") }} <span v-if="errorCode">({{ errorCode }})</span></span
            ></v-alert
          >
        </v-col>
      </v-row>

      <!-- UPLOAD INDICATOR -->
      <v-row v-if="lockSubmit && !documentsEmpty">
        <v-col cols="12" md="10" offset="0" offset-md="1" class="payment-margin mb-5">
          <DocumentUploadIndicator />
        </v-col>
      </v-row>

      <!-- PAYMENT ELEMENT -->
      <v-row v-if="stripePaymentAvailable">
        <v-col cols="12" md="10" offset="0" offset-md="1">
          <div id="payment-element">
            <!-- Mount the Payment Element here -->
          </div>
        </v-col>
      </v-row>
      <div class="text-center mt-3 mb-9">
        <v-btn
          tile
          :color="uiColor"
          large
          v-on:click.prevent="requestUserSecret()"
          v-if="!lockSubmit && paymentElementLoaded"
          ><span v-if="documentsEmpty">{{ $t("register_and_pay") }}</span
          ><span v-if="!documentsEmpty">{{ $t("register_and_pay_and_upload") }}</span></v-btn
        >
        <v-progress-circular
          :size="50"
          :width="7"
          :color="uiColor"
          indeterminate
          v-if="lockSubmit || !paymentElementLoaded"
          :aria-label="$t('loading')"
          :aria-busy="lockSubmit || !paymentElementLoaded"
          aria-live="polite"
        ></v-progress-circular>
      </div>

      <!-- BACK BUTTON -->
      <div>
        <v-btn
          tile
          :color="uiColor"
          :disabled="lockSubmit"
          class="back-button back-button--margin-top"
          v-on:click="$emit('back-button-clicked')"
          >{{ $t("back") }}</v-btn
        >
      </div>
    </div>
  </v-container>
</template>

<script>
import { getUserSecret } from "../services/CheckoutService";
import RegInfoSummary from "./RegInfoSummary";
import { uploadDocuments } from "../services/DocumentUploadService";
import DocumentUploadIndicator from "./DocumentUploadIndicator";
import { getStripeAmount } from "../utils/utils";
import { logStep } from "../services/LogService";

export default {
  name: "Checkout",
  components: {
    RegInfoSummary,
    DocumentUploadIndicator,
  },
  mounted() {
    this.includeStripe(
      "js.stripe.com/v3/",
      function () {
        if (this.price.finalPrice > 0) {
          //STRIPE SET-UP
          if (typeof window !== "undefined") {
            this.stripe = window.Stripe(this.$store.getters["registration/providerConfig"].stripePk, {
              apiVersion: "2025-04-30.basil",
            });
          }
          const appearance = {
            theme: "stripe",
            variables: {
              colorPrimary: this.$store.getters["registration/uiColor"],
              colorBackground: "#ffffff",
              colorText: "#000000de",
              colorDanger: "#f0404f",
              fontFamily: "Roboto, sans-serif",
              spacingUnit: "4px",
              borderRadius: "0px",
            },
          };
          const options = {
            mode: "payment",
            currency: this.price.currency.toLowerCase(),
            amount: getStripeAmount(this.price.currency, this.price.finalPrice),
            locale: this.locale,
            loader: "always",
            appearance,
          };
          console.log("stripeOptions", options);
          this.elements = this.stripe.elements(options);

          // Payment Element
          this.paymentElement = this.elements.create("payment");
          this.paymentElement.mount("#payment-element");
          this.paymentElement.on("ready", () => {
            this.paymentElementLoaded = true;
          });

          // Express Checkout Element
          const expressCheckoutOptions = {
            paymentMethods: {
              applePay: "always",
              googlePay: "always",
            },
          };
          this.expressCheckoutElement = this.elements.create("expressCheckout", expressCheckoutOptions);
          this.expressCheckoutElement.mount("#express-checkout-element");
          this.expressCheckoutElement.on("loaderror", (event) => {
            console.log("Failed to load express checkout element", event);
          });
          this.expressCheckoutElement.on("click", (event) => {
            const options = {
              emailRequired: true,
              billingAddressRequired: this.providerConfig.collectBillingAddress,
            };
            event.resolve(options);
          });
          this.expressCheckoutElement.on("confirm", async (event) => {
            const paymentResult = await this.requestUserSecret();
            console.log("paymentResult", paymentResult);
            if (paymentResult === false) {
              console.log("paymentFailed will get called");
              event.paymentFailed();
            }
          });
        }
      }.bind(this)
    );
  },
  data() {
    return {
      stripe: "",
      elements: "",
      paymentElement: "",
      expressCheckoutElement: "",
      lockSubmit: false,
      locale: this.$store.getters["registration/locale"],
      price: this.$store.getters["registration/calculatedPrice"],
      paymentSuccessful: false,
      paymentFailed: false,
      saveFailed: false,
      documentsEmpty: this.$store.getters["registration/documentsEmpty"],
      documents: this.$store.getters["registration/documents"],
      random: this.$store.getters["registration/registrationRandom"],
      uiColor: this.$store.getters["registration/uiColor"],
      errorCode: null,
      tenant: this.$store.getters["registration/tenant"],
      provider: this.$store.getters["registration/provider"],
      providerConfig: this.$store.getters["registration/providerConfig"],
      paymentElementLoaded: false,
    };
  },
  methods: {
    includeStripe(URL, callback) {
      let documentTag = document,
        tag = "script",
        object = documentTag.createElement(tag),
        scriptTag = documentTag.getElementsByTagName(tag)[0];
      object.src = "https://" + URL;
      if (callback) {
        object.addEventListener(
          "load",
          function (e) {
            callback(null, e);
          },
          false
        );
      }
      scriptTag.parentNode.insertBefore(object, scriptTag);
    },
    getDataToSend(paymentMethod) {
      let regInfo = this.$store.getters["registration/registrationData"];
      regInfo.paymentInfo.paymentMethod = paymentMethod;
      regInfo.paymentInfo.apiVersion = "2025-04-30.basil";
      return regInfo;
    },
    async requestUserSecret() {
      this.lockSubmit = true;

      // Trigger form validation and wallet collection
      const { error: submitError } = await this.elements.submit();
      if (submitError) {
        console.log("Error occured!", submitError);
        this.paymentFailed = true;
        this.lockSubmit = false;
        return false;
      }

      // Document upload
      if (!this.documentsEmpty) {
        this.uploadResult = await uploadDocuments(
          this.$store.getters["registration/env"].documentUploadUrl,
          this.documents,
          this.tenant,
          this.random
        );
        if (Object.keys(this.uploadResult.failed).length === 0) {
          this.documentUploadSuccess = true;
          this.documentUploadError = false;
        } else {
          if (Object.values(this.uploadResult.failed[Object.keys(this.uploadResult.failed)[0]])[0].error) {
            this.documentUploadError = Object.values(
              this.uploadResult.failed[Object.keys(this.uploadResult.failed)[0]]
            )[0].error;
          } else {
            this.documentUploadError = true;
          }
        }
      }

      //Submit registration, receive payment intent and finalize payment
      const userSecret = await getUserSecret(
        this.$store.getters["registration/env"].backendUrl,
        this.getDataToSend("card")
      );
      if (userSecret && !userSecret.error) {
        this.saveFailed = false;
        console.log("user secret successfully obtained");
        logStep(
          this.random,
          "RegistrationCompletedStripe",
          this.$store.getters["registration/country"],
          this.$store.getters["registration/language"],
          this.$store.getters["registration/certId"],
          "finish"
        );
        return await this.payWithStripe(userSecret.clientSecret);
      } else {
        console.log("user secret failed");
        this.lockSubmit = false;
        this.saveFailed = true;
        if (userSecret?.error && userSecret?.status) {
          this.errorCode = userSecret.status;
        }
        logStep(
          this.random,
          "RegistrationFailedStripe",
          this.$store.getters["registration/country"],
          this.$store.getters["registration/language"],
          this.$store.getters["registration/certId"],
          "finish"
        );
        return false;
      }
    },
    async payWithStripe(clientSecret) {
      this.lockSubmit = true;
      const confirmParams = {
        return_url:
          this.locale === "de"
            ? `${this.$store.getters["registration/env"].stripeReturnUrlDe}/${this.provider}`
            : `${this.$store.getters["registration/env"].stripeReturnUrlEn}/${this.provider}`,
      };
      // if the provider is configured to issue invoices via Stripe
      // the billing address must be also sent with the payment info to Stripe
      if (this.providerConfig.collectBillingAddress) {
        confirmParams["payment_method_data"] = {
          billing_details: {
            name: `${this.billingInfo.firstname} ${this.billingInfo.lastname}`,
            email: this.billingInfo.email,
            address: {
              city: this.billingInfo.address.city,
              country: this.billingInfo.address.country,
              line1: this.billingInfo.address.line1,
              line2: this.billingInfo.address.line2,
              postal_code: this.billingInfo.address.zip,
              state: this.billingInfo.address.state,
            },
          },
        };
      }
      const result = await this.stripe.confirmPayment({
        elements: this.elements,
        clientSecret,
        confirmParams,
      });
      if (result.error) {
        // Show error to your customer
        console.log("Error occured!", result.error.message);
        this.paymentFailed = true;
        this.lockSubmit = false;
        return false;
      } else if (result.paymentIntent && result.paymentIntent.status === "succeeded") {
        // The payment succeeded!
        console.log("Payment successfully competed");
        this.paymentSuccessful = true;
        this.registrationCompleted = true;
        this.paymentFailed = false;
        this.lockSubmit = false;
        return true;
      } else {
        // Show error to your customer
        console.log("Error occured!");
        this.paymentFailed = true;
        this.lockSubmit = false;
        return false;
      }
    },
  },
  computed: {
    billingInfo() {
      return this.$store.getters["registration/billingInfo"];
    },
    stripePaymentAvailable() {
      return this.$store.getters["registration/stripePaymentAvailable"];
    },
    invoicePaymentAvailable() {
      return this.$store.getters["registration/invoicePaymentAvailable"];
    },
    registrationCompleted: {
      get() {
        return this.$store.getters["registration/registrationCompleted"];
      },
      set(value) {
        this.$store.dispatch("registration/updateRegistrationCompleted", value);
      },
    },
    documentUploadSuccess: {
      get() {
        return this.$store.getters["registration/documentUploadSuccess"];
      },
      set(value) {
        this.$store.dispatch("registration/updateDocumentUploadSuccess", value);
      },
    },
    documentUploadError: {
      get() {
        return this.$store.getters["registration/documentUploadError"];
      },
      set(value) {
        this.$store.dispatch("registration/updateDocumentUploadError", value);
      },
    },
    uploadResult: {
      get() {
        return this.$store.getters["registration/uploadResult"];
      },
      set(value) {
        this.$store.dispatch("registration/updateUploadResult", value);
      },
    },
  },
};
</script>

<style scoped>
.main-container {
  height: 100%;
}
@media (max-width: 960px) {
  .main-container {
    height: fit-content !important;
  }
}
@media (max-width: 450px) {
  .v-tabs {
    max-width: 300px !important;
  }
}
@media (max-width: 320px) {
  .v-tabs {
    max-width: 250px !important;
  }
}
.v-tabs {
  width: auto;
}
.stripe-logo {
  height: 22px;
  width: auto;
  float: right;
}
.payment-container {
  height: fit-content;
}
</style>
